import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session token for multipart uploads. Populated at app boot by
// AuthContext.checkAuth() via setSessionTokenForUploads() — sourced from
// the /api/auth/me response which reliably succeeds (the page literally
// can't render without it). This eliminates the dependency on a separate
// /auth/token GET that the production Cloudflare config has been
// intermittently failing on. As a last-resort fallback, we still try
// /auth/token if AuthContext hasn't called us yet (e.g. an upload fires
// before the auth boot completes).
let _cachedSessionToken = null;

export function setSessionTokenForUploads(token) {
  if (token) _cachedSessionToken = token;
}

export async function getSessionTokenForUploads() {
  return await _getSessionToken();
}

async function _getSessionToken() {
  if (_cachedSessionToken) return _cachedSessionToken;
  try {
    const t = await axios.get(`${API}/auth/token`, { withCredentials: true });
    _cachedSessionToken = t.data?.session_token || null;
  } catch { /* fall through — auth/me-sourced token may already be on its way */ }
  return _cachedSessionToken;
}

async function _postUpload(formData) {
  const token = await _getSessionToken();
  // Triple-channel auth — survives even the strictest Cloudflare configs:
  //   1. Cookie  (via withCredentials)
  //   2. Header  (Authorization: Bearer)
  //   3. Form field `session_token` inside the multipart body
  // Form-body bytes always reach the origin because Cloudflare can't
  // mangle them without breaking the upload itself. The backend honours
  // all three channels; whichever survives the proxy wins.
  if (token) {
    let hasToken = false;
    for (const [k] of formData.entries()) { if (k === 'session_token') { hasToken = true; break; } }
    if (!hasToken) formData.append('session_token', token);
  }
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return axios.post(`${API}/documents/upload`, formData, {
    withCredentials: true,
    headers,
  });
}

/**
 * POSTs to /api/documents/upload with duplicate detection.
 *
 * Backend returns HTTP 409 with `{detail: {code, message, existing_doc}}`
 * when the same file (by SHA-256 content hash) is already uploaded against
 * the same entity. We prompt the user with the existing-doc context and
 * retry with `force=true` if they choose Upload anyway.
 *
 * @param {FormData} formData - already prepared (file, entity_type, entity_id, doc_type, etc.)
 * @returns {Promise<{data: any, skipped: boolean}>}
 *   - data: the server's saved doc on success (or null if skipped)
 *   - skipped: true when the user dismissed the duplicate dialog
 */
export async function uploadDocumentWithDedupe(formData) {
  try {
    const res = await _postUpload(formData);
    return { data: res.data, skipped: false };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // If the FIRST attempt 401'd, our cached token may be stale (post-redeploy
    // or session rotation). Invalidate + retry once.
    if (status === 401 && _cachedSessionToken) {
      _cachedSessionToken = null;
      try {
        const res2 = await _postUpload(formData);
        return { data: res2.data, skipped: false };
      } catch (err2) {
        throw err2;
      }
    }

    const isDup = status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_document';
    if (!isDup) throw err;

    const existing = detail.existing_doc || {};
    const fname = existing.original_filename || '(unknown)';
    const dt = existing.doc_type || 'general';
    const when = existing.created_at ? new Date(existing.created_at).toLocaleDateString() : '';
    const promptMsg =
      `This file is already uploaded as:\n\n` +
      `  • ${fname}\n` +
      `  • Type: ${dt}\n` +
      (when ? `  • Uploaded: ${when}\n` : '') +
      `\nThe existing copy is kept by default.\n\n` +
      `Click OK to upload anyway (creates a second copy), or Cancel to skip.`;

    if (!window.confirm(promptMsg)) {
      return { data: null, skipped: true };
    }

    // Force the upload bypassing dedup. The FormData object is single-use
    // (its underlying stream gets consumed), so we rebuild it from entries.
    const retry = new FormData();
    for (const [k, v] of formData.entries()) {
      // Don't double-copy session_token — _postUpload will re-append from cache.
      if (k === 'session_token') continue;
      retry.append(k, v);
    }
    retry.append('force', 'true');
    const res2 = await _postUpload(retry);
    return { data: res2.data, skipped: false };
  }
}
